import {
  ChangeDetectionStrategy,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  computed,
  inject,
} from '@angular/core';
import { ModalComponent } from '../../../ui-components/modal/modal.component';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import {
  AllowancePeriod,
  CreditAccount,
  PayoutType,
} from '@enrollhere/classes';
import { EnrichedPayout } from '../../../../services/payout-api.service';
import { LoadingComponent } from '../../../ui-components/loading/loading.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { AccountCardComponent } from '../../../account/account-card/account-card.component';
import { WalletKindBadgeComponent } from '../../../account/wallet-kind-badge/wallet-kind-badge.component';
import {
  ModalContainerService,
  ShowModalComponents,
} from '../../../../services/modal-container.service';
import { UserCacheService } from '../../../../services/user-cache.service';
import {
  creditTransactionTypeMap,
  payoutTypeMap,
  typeMap,
} from '../../../../data/payoutType';
import { allowancePeriodMap } from '../../../../data/account';
import {
  getFundingSource,
  getTransactionDirection,
  transactionDirectionMap,
} from '../../../../data/walletKind';
import { selectStaticTeamsByAgencyId } from '../../../../store';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-payouts-detail-modal',
  standalone: true,
  imports: [
    AccountCardComponent,
    CurrencyPipe,
    DatePipe,
    FaIconComponent,
    LoadingComponent,
    ModalComponent,
    WalletKindBadgeComponent,
  ],
  templateUrl: './payouts-detail-modal.component.html',
  styleUrl: './payouts-detail-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PayoutsDetailModalComponent implements AfterViewInit {
  private cdr = inject(ChangeDetectorRef);
  private modalContainerService = inject(ModalContainerService);
  /** @deprecated Direct NgRx Store usage is deprecated. Use API endpoints instead. */
  private store = inject(Store);
  userCacheService = inject(UserCacheService);

  @Input() openWith?: {
    payment: EnrichedPayout;
    userId: string;
    parentFilter: any;
  };

  @Output() modalDismissedFired = new EventEmitter<boolean>();

  @ViewChild('modal') modal?: ModalComponent;

  faArrowRight = faArrowRight;

  parentFilter?: any;
  payment?: EnrichedPayout;
  userId?: string;

  teamHierarchy$ = computed(() => {
    const agencyId = this.payment?.agency?.id;
    return agencyId
      ? this.store.select(selectStaticTeamsByAgencyId(agencyId))
      : null;
  });

  payoutTypeMap = payoutTypeMap;
  creditTransactionTypeMap = creditTransactionTypeMap;
  typeMap = typeMap;

  /** "Monthly", "Weekly", … for an allowance period enum value */
  allowancePeriodName(period?: AllowancePeriod): string | undefined {
    if (period === undefined) return undefined;
    return allowancePeriodMap[period]?.name;
  }

  /** Where the money came from / which pool it was taken from */
  fundingSource() {
    return getFundingSource(this.payment);
  }

  /** Added vs Spent, from the sign of the amount */
  direction() {
    const direction = getTransactionDirection(this.payment);
    return direction ? transactionDirectionMap[direction] : undefined;
  }

  /** Negative top-up rows are refunds of a card top-up; see payouts-table. */
  isTopUpRefund(payment: EnrichedPayout): boolean {
    const isTopUp =
      payment.payoutType === PayoutType.PT_TOP_UP ||
      payment.payoutType === PayoutType.PT_AUTO_TOP_UP;
    return isTopUp && (payment.transaction?.amount ?? 0) < 0;
  }

  ngAfterViewInit() {
    if (this.openWith) {
      this.open(
        this.openWith.payment,
        this.openWith.userId,
        this.openWith.parentFilter
      );
      this.cdr.detectChanges();
    }
  }

  open(payment: EnrichedPayout, userId: string, parentFilter: any) {
    console.log('open payment', { payment, userId });
    this.payment = payment;
    this.parentFilter = parentFilter;
    this.userId = userId;

    if (this.payment?.executive?.aggregatedExecutiveRule?.executiveLadder) {
      for (const user of this.payment?.executive?.aggregatedExecutiveRule
        ?.executiveLadder ?? []) {
        this.userCacheService.addToCache(user);
      }
    }

    this.modal?.show();
  }

  showPayout(payment: any) {
    console.log('editPayout', payment);
    if (!this.userId) {
      return;
    }
    // this.payoutsDetailModal?.open(payment, this.userId, this.parentFilter);

    this.modalContainerService.open(ShowModalComponents.PAYOUT, {
      payment,
      userId: this.userId,
      parentFilter: this.parentFilter,
    });
  }

  modalDismissed() {
    delete this.payment;
    delete this.parentFilter;
    delete this.userId;

    this.modalDismissedFired.emit();
    this.cdr.detectChanges();
  }

  getAccountTeams(account: CreditAccount): string[] {
    if (!account.permanentTags?.list) return [];
    return account.permanentTags.list;
  }
}
